/**
 * Passkey intelligence — Zod schemas.
 *
 * The overview payload answers "how is this tenant doing on the
 * passkey adoption curve?" with a small set of numbers and a
 * 30-day time series we can chart.
 */

import { z } from "zod";

export const passkeyOverviewSchema = z.object({
  /** Users with at least one registered passkey. */
  users_with_passkey: z.number().int().nonnegative(),
  /** Total users in the tenant. */
  users_total: z.number().int().nonnegative(),
  /** Passkeys minted in the last 30 days. */
  passkeys_added_30d: z.number().int().nonnegative(),
  /** Passkeys revoked in the last 30 days. */
  passkeys_revoked_30d: z.number().int().nonnegative(),
  /** Sign-ins satisfied by a passkey in the last 30 days. */
  passkey_logins_30d: z.number().int().nonnegative(),
  /** Sign-ins that fell back to OTP in the last 30 days. */
  fallback_logins_30d: z.number().int().nonnegative(),
  /** Average passkeys per user with a passkey. */
  avg_passkeys_per_user: z.number().nonnegative(),
  /** Breakdown by authenticator type (display_name → count). */
  authenticator_breakdown: z.record(z.string(), z.number().int().nonnegative()),
  /** Daily sample counts — 30 entries ordered oldest → newest. */
  daily_series: z.array(
    z.object({
      date: z.string(),
      passkey_logins: z.number().int().nonnegative(),
      fallback_logins: z.number().int().nonnegative(),
      new_passkeys: z.number().int().nonnegative(),
    }),
  ),
});
export type PasskeyOverview = z.infer<typeof passkeyOverviewSchema>;
